import { appointmentStatus } from '../../../../constants';
import {
  getDateTimeString,
  getFullName,
  stringDate,
  stringTime,
} from '../../../../util';
import { Divider, Space, Table, Tag, Typography } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect } from 'react';
import { getAppointmentByCustomer } from '../../../../appRedux/actions';
import { getAllDiaries } from '../../../../appRedux/actions/Diary';
import { arraySort } from '../../../../util/arraySort';
import {
  actionDictionary,
  changesDictionary,
} from '../../../../constants/Diary.const';

const parseChanges = (changes) => {
  if (!changes) return {};
  if (typeof changes === 'object') return changes;

  try {
    return JSON.parse(changes);
  } catch {
    return {};
  }
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return value.name || value.id || '—';
  return changesDictionary[value] || String(value);
};

const formatChangeValue = (field, value) =>
  field === 'appointmentNumber' && value !== null && value !== undefined
    ? `C${value}`
    : formatValue(value);

export const CustomerAppointmentsHistory = ({ appointments }) => {
  const dispatch = useDispatch();
  const { appointmentByCustomer } = useSelector((state) => state.agenda);
  const { diaries } = useSelector((state) => state.diary);

  const appointmentSorter = arraySort(appointmentByCustomer?.items);

  useEffect(() => {
    dispatch(getAppointmentByCustomer({ customerId: appointments }));
    dispatch(getAllDiaries({ customerId: appointments, take: 1000 }));
  }, [dispatch, appointments]);

  const columns = [
    {
      title: 'Originó',
      dataIndex: 'user',
      key: 'user',
      render: (user, appointment) =>
        user
          ? getFullName(user)
          : appointment.appointmentMethod || 'E-commerce/App',
    },
    {
      title: 'Cita',
      dataIndex: 'appointmentNumber',
      key: 'appointmentNumber',
    },
    {
      title: 'Fecha de cita',
      dataIndex: 'date',
      key: 'date',
      render: (date) => <Tag color="green"> {stringDate(date)} </Tag>,
    },
    {
      title: 'Hora de cita',
      dataIndex: 'startTime',
      key: 'startTime',
      render: (startTime) => <Tag color="blue"> {stringTime(startTime)} </Tag>,
    },
    {
      title: 'Sucursal',
      dataIndex: 'name',
      key: 'branch',
      render: (_, { branch }) => <>{branch?.name}</>,
    },
    {
      title: 'Atendió',
      dataIndex: 'attendedBy',
      key: 'attendedBy',
      render: (attendedBy) => (
        <>
          {attendedBy?.name ? attendedBy?.name : '-'} {attendedBy?.lastName}{' '}
          {attendedBy?.motherLastName}
        </>
      ),
      path: [
        'attendedBy.name',
        'attendedBy.lastName',
        'attendedBy.motherLastName',
      ],
    },

    {
      title: 'Tratamiento',
      dataIndex: 'treatment',
      key: 'treatment',
      render: (treatment, appointment) => {
        const appliedTreatment = appointment?.appliedTreatment;
        const treatmentChanged =
          !!appliedTreatment?.id && appliedTreatment.id !== treatment?.id;

        return treatmentChanged
          ? `${treatment?.name} → ${appliedTreatment.name}`
          : treatment?.name || '-';
      },
      path: 'treatment.name',
    },
    {
      title: 'Observaciones',
      dataIndex: 'observations',
      key: 'observations',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status, appointment) =>
        appointment.isEdited ? (
          <Tag color="orange">{appointmentStatus.edited?.value}</Tag>
        ) : (
          <Tag color={`#${appointmentStatus[status]?.color}`}>
            {appointmentStatus[status]?.value}
          </Tag>
        ),
      path: 'status',
      formatter: (status) => appointmentStatus[status]?.value,
    },
  ];

  const movementColumns = [
    {
      title: 'Cita',
      dataIndex: 'appointmentNumber',
      key: 'appointmentNumber',
      render: (number) => `C${number}`,
    },
    {
      title: 'Movimiento',
      dataIndex: 'action',
      key: 'action',
      render: (action) => actionDictionary[action] || action,
    },
    {
      title: 'Cambios',
      dataIndex: 'changes',
      key: 'changes',
      width: 340,
      render: (changes) => {
        const entries = Object.entries(parseChanges(changes));

        return entries.length ? (
          <Space direction="vertical" size={8}>
            {entries.map(([field, values]) => (
              <div key={field}>
                <strong style={{ display: 'block' }}>
                  {changesDictionary[field] || field}
                </strong>
                <span style={{ whiteSpace: 'nowrap' }}>
                  {formatChangeValue(field, values?.before)} →{' '}
                  {formatChangeValue(field, values?.after)}
                </span>
              </div>
            ))}
          </Space>
        ) : (
          'Sin cambios de datos'
        );
      },
    },
    {
      title: 'Responsable',
      dataIndex: 'user',
      key: 'responsible',
      render: (user, movement) =>
        user ? getFullName(user) : getFullName(movement.customer) || 'Cliente',
    },
    {
      title: 'Fecha y hora',
      dataIndex: 'created_at',
      key: 'created_at',
      render: getDateTimeString,
    },
  ];

  return (
    <>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={appointmentSorter}
        pagination={{ pageSize: 5 }}
        scroll={{ x: 900 }}
      />
      <Divider />
      <Typography.Title level={5}>Historial de movimientos</Typography.Title>
      <Table
        rowKey="id"
        columns={movementColumns}
        dataSource={diaries}
        pagination={{ pageSize: 5 }}
        scroll={{ x: 900 }}
      />
    </>
  );
};
